import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, setToken } from "@/lib/api";

/**
 * AuthContext — Release 1.2-P3-B.
 *
 * Single source of truth for authentication AND subscription state on the
 * frontend. Every subscription-aware component (LimitBadge, FounderBadge,
 * TrialBanner, Header, Dashboard, feature gates) MUST consume this context;
 * no component may derive premium/founder/teacher state from ad-hoc reads of
 * `user.subscription_tier`.
 *
 * Derived flags exposed:
 *   - `isPremium`  — any active paid tier (premium / teacher / founder / district / enterprise)
 *   - `isFounder`  — active founder tier OR grandfathered "Founder Member" badge
 *   - `isTeacher`  — active teacher tier
 *   - `tier`       — canonical tier string
 *
 * Event bus:
 *   - Fires `sparkread:subscription-changed` after every successful `refresh()`.
 *     Components that cache subscription-derived data (e.g. LimitBadge, which
 *     re-fetches `/limits/me`) MUST listen for this event to stay in sync.
 */
const AuthContext = createContext(null);

const PAID_TIERS = new Set(["premium", "teacher", "founder", "district", "enterprise"]);

function deriveFlags(user) {
  if (!user) {
    return {
      tier: null,
      isPremium: false,
      isFounder: false,
      isTeacher: false,
      isStudent: false,
    };
  }
  const tier = user.subscription_tier || "free";
  const active = !!user.subscription_active;
  const isPremium = active && PAID_TIERS.has(tier);
  const isFounder =
    (active && tier === "founder") ||
    (Array.isArray(user.badges) && user.badges.includes("Founder Member"));
  const isTeacher = active && tier === "teacher";
  const isStudent = user.role === "student";
  return { tier, isPremium, isFounder, isTeacher, isStudent };
}

function dispatchSubscriptionChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("sparkread:subscription-changed"));
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const tok = localStorage.getItem("sr_token");
    if (!tok) {
      setUser(null);
      setLoading(false);
      dispatchSubscriptionChanged();
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      dispatchSubscriptionChanged();
    } catch {
      setToken(null);
      setUser(null);
      dispatchSubscriptionChanged();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    dispatchSubscriptionChanged();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.token);
    setUser(data.user);
    dispatchSubscriptionChanged();
    return data.user;
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    setToken(null);
    setUser(null);
    dispatchSubscriptionChanged();
  };

  const flags = useMemo(() => deriveFlags(user), [user]);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, ...flags }),
    [user, loading, login, register, logout, refresh, flags],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
